/**
 * StatusIndicator
 * Minimal status display with animated dot and tooltip.
 */

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import type { RenderStatus, TransmissionStatus } from '../types';
import { STATUS_DISPLAY } from './constants';

interface StatusIndicatorProps {
  renderStatus: RenderStatus;
  transmissionStatus?: TransmissionStatus | null;
  transmitted?: boolean;
  className?: string;
}

export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  renderStatus,
  transmissionStatus,
  transmitted,
  className,
}) => {
  // Transmitted status takes priority
  const status = transmitted
    ? transmissionStatus || 'sent'
    : transmissionStatus || renderStatus;

  const config = STATUS_DISPLAY[status] ?? STATUS_DISPLAY.draft;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 text-xs font-medium cursor-default',
            config.className,
            className,
          )}
        >
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              config.dotClassName,
              config.animate && 'animate-pulse',
            )}
          />
          {config.label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">
        {config.description}
      </TooltipContent>
    </Tooltip>
  );
};
